import React from 'react';
import { Submission, Task, ThreadMessage } from '../../../types';
import { MediaDisplay, formatTimestamp } from '../../shared/media';
import { ConversationThread } from '../../thread/ConversationThread';
import './SubmissionCard.css';
import './SubmissionCard.glass.css';

export interface SubmissionCardProps {
  submission: Submission;
  task: Task | undefined;
  /** Pre-loaded thread messages for this submission */
  threadMessages?: ThreadMessage[];
  /** Count of unread researcher messages */
  unreadCount?: number;
  /** Thread authoring context — needed to send replies */
  threadContext?: {
    currentUserUid: string;
    currentUserName: string;
    organizationId: string;
  };
  /** Addendum: called when participant wants to add to this submission */
  onAddendum?: () => void;
  /** Human-readable time remaining for addendum — e.g. "23h left" */
  addendumTimeLeft?: string;
  /** Redo: called when participant taps "Redo this task" on a redo-requested submission */
  onRedo?: () => void;
  /** Researcher's reason for requesting the redo */
  redoReason?: string;
  /** Whether a newer redo submission already exists for this task (hides the redo button) */
  hasNewerRedo?: boolean;
  /** Whether this is a redo submission (nested display) */
  isRedoChild?: boolean;
  /** Attempt number for redo submissions */
  attemptNumber?: number;
}

export const SubmissionCard: React.FC<SubmissionCardProps> = ({
  submission,
  task,
  threadMessages,
  unreadCount,
  threadContext,
  onAddendum,
  addendumTimeLeft,
  onRedo,
  redoReason,
  hasNewerRedo,
  isRedoChild,
  attemptNumber,
}) => {
  const taskName = task?.title || task?.prompt || 'Task';
  const ts = submission.createdAt || submission.timestamp;
  const { relative, date, receipt } = formatTimestamp(ts);
  const isText = submission.mediaType === 'text';

  const hasThread = (threadMessages && threadMessages.length > 0) || false;
  const hasUnread = (unreadCount ?? 0) > 0;

  const unreadResearcherName = hasUnread && threadMessages
    ? threadMessages.filter(m => m.senderRole === 'researcher' && !m.readByRecipient).slice(-1)[0]?.senderName
    : null;

  const needsRedo = !!submission.redoRequestedAt && !hasNewerRedo;

  return (
    <div className={`sc-card ${isRedoChild ? 'sc-card--redo-child' : ''} ${needsRedo ? 'sc-card--needs-redo' : ''}`}>
      {/* Redo child label */}
      {isRedoChild && (
        <div className="sc-redo-child-label">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="1,4 1,10 7,10" />
            <path d="M3.51 15a9 9 0 1 0 .49-4.95" />
          </svg>
          Redo #{attemptNumber || 2}
        </div>
      )}

      {/* ═══ ZONE 1: Media — shared component ═══ */}
      <MediaDisplay submission={submission} />

      {/* ═══ ZONE 2: Action row — task label, timestamp, badges ═══ */}
      <div className="sc-body">
        <div className="sc-task-label">{taskName}</div>
        {submission.notes && !isText && <div className="sc-notes">{submission.notes}</div>}
        <div className="sc-meta">
          <div className="sc-check">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6E7A5A" strokeWidth="3" strokeLinecap="round">
              <polyline points="20,6 9,17 4,12" />
            </svg>
          </div>
          <span className="sc-relative">{receipt || relative}</span>
          {!receipt && date && (
            <>
              <span className="sc-separator">&middot;</span>
              <span className="sc-date">{date}</span>
            </>
          )}
          {submission.isReviewed && (
            <>
              <span className="sc-separator">&middot;</span>
              <span className="sc-reviewed">Reviewed</span>
            </>
          )}
        </div>
        {onAddendum && (
          <button className="sc-addendum-btn" onClick={onAddendum}>
            + Add more to this response
            {addendumTimeLeft && <span className="sc-addendum-time">{addendumTimeLeft}</span>}
          </button>
        )}

        {/* Redo requested banner */}
        {needsRedo && (
          <div className="sc-redo-banner">
            <div className="sc-redo-banner-header">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <polyline points="1,4 1,10 7,10" />
                <path d="M3.51 15a9 9 0 1 0 .49-4.95" />
              </svg>
              <span>Redo requested</span>
            </div>
            {redoReason && <div className="sc-redo-banner-reason">{redoReason}</div>}
            {onRedo && (
              <button className="sc-redo-banner-btn" onClick={onRedo}>
                Redo this task
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>

      {/* ═══ ZONE 3: Thread — inline comments ═══ */}
      {(hasThread || hasUnread) && threadContext && (
        <div className="sc-thread">
          {hasUnread && unreadResearcherName && (
            <div className="ct-alert">
              <span className="ct-alert-dot" />
              <span className="ct-alert-text">New feedback from {unreadResearcherName}</span>
            </div>
          )}

          <ConversationThread
            submissionId={submission.id}
            organizationId={threadContext.organizationId}
            projectId={submission.projectId}
            participantId={submission.participantId}
            currentUserUid={threadContext.currentUserUid}
            currentUserName={threadContext.currentUserName}
            currentUserRole="participant"
            messages={threadMessages}
            taskName={taskName}
          />
        </div>
      )}

      {!hasThread && threadContext && (
        <div className="sc-thread sc-thread-empty">
          <ConversationThread
            submissionId={submission.id}
            organizationId={threadContext.organizationId}
            projectId={submission.projectId}
            participantId={submission.participantId}
            currentUserUid={threadContext.currentUserUid}
            currentUserName={threadContext.currentUserName}
            currentUserRole="participant"
            messages={[]}
            taskName={taskName}
          />
        </div>
      )}
    </div>
  );
};
